import React, { useState, useEffect } from 'react';
import { useCatalogStore, CatalogItem } from '@/store/catalogStore';
import { useLojaAtivaStore } from '@/store/lojaAtivaStore';
import { apiCall } from '@/services/api';
import { toast } from 'react-hot-toast';
import {
  X,
  Search,
  Package,
  Wrench,
  ImageOff,
  Send,
  Loader2,
  Layers,
  ExternalLink,
  Store,
  DollarSign
} from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import { sendCatalogParts } from '@/lib/catalog-send';
import { createPortal } from 'react-dom';

interface CatalogPickerProps {
  open: boolean;
  leadId: string;
  leadLojaId?: string | null;
  onClose: () => void;
  onSent?: () => void;
}

export const CatalogPicker: React.FC<CatalogPickerProps> = ({
  open,
  leadId,
  leadLojaId,
  onClose,
  onSent,
}) => {
  const { items, loading, fetchItems } = useCatalogStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFilter, setTipoFilter] = useState<'todos' | 'produto' | 'servico'>('todos');
  const [sendingItemId, setSendingItemId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const activeLojaId = useLojaAtivaStore.getState().lojaAtivaId;
      fetchItems({
        tipo: tipoFilter,
        loja_id: leadLojaId || activeLojaId || undefined,
        q: searchTerm,
        active_only: true,
      });
    }
  }, [open, tipoFilter, leadLojaId, fetchItems, searchTerm]);

  if (!open) return null;

  const handleSendItem = async (item: CatalogItem) => {
    try {
      setSendingItemId(item.id);

      const formattedPrice = formatCurrency(item.preco, item.moeda);
      const link = item.link_produto || item.link_pagamento || '';

      let formattedText = `*${item.nome}*\n`;

      // Descrição COMPLETA (não truncar)
      if (item.descricao) {
        formattedText += `\nDescrição: ${item.descricao}\n`;
      }

      // Campos de produto
      if (item.tipo === 'produto') {
        if (item.tamanho) formattedText += `\nTamanho: ${item.tamanho}`;
        if (item.cores) formattedText += `\nCores: ${item.cores}`;
      }

      // Campos de serviço
      if (item.tipo === 'servico' && item.entrega) {
        formattedText += `\nEntrega: ${item.entrega}`;
      }

      // Preço
      formattedText += `\n\nValor: ${formattedPrice}`;

      // Condições de pagamento
      if (item.condicoes_pagamento) {
        formattedText += `\nForma de pagamento: ${item.condicoes_pagamento}`;
      }

      // Campos personalizados (custom_fields)
      if (item.custom_fields && Array.isArray(item.custom_fields) && item.custom_fields.length > 0) {
        formattedText += `\n\n*Especificações:*`;
        for (const cf of item.custom_fields) {
          if (cf.key && cf.value) {
            formattedText += `\n• ${cf.key}: ${cf.value}`;
          }
        }
      }

      // Link
      if (link) {
        formattedText += `\n\nLink: ${link}`;
      }

      const images = (item.imagens || []).filter(Boolean);
      // Fecha o modal na hora: o progresso segue no toast (imagens em ordem, depois 1 texto).
      onClose();
      toast.loading(`Enviando ${item.nome}...`, { id: 'catalog-send' });

      const r = await sendCatalogParts({
        leadId,
        nome: item.nome,
        images,
        text: formattedText,
        batchId: crypto.randomUUID(),
        post: (path, body) =>
          apiCall(path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }),
        onProgress: (done, total) =>
          toast.loading(`Enviando ${item.nome} (${done}/${total})...`, { id: 'catalog-send' }),
      });

      if (r.errors.length === 0) {
        toast.success(
          images.length > 0
            ? `"${item.nome}" enviado (${r.imagesSent}/${r.imagesTotal} imagens + detalhes)!`
            : `"${item.nome}" enviado com sucesso!`,
          { id: 'catalog-send' }
        );
        onSent?.();
      } else {
        console.error('[CatalogPicker] Falhas no envio:', r.errors);
        const delivered = r.imagesSent > 0 || r.textSent;
        if (delivered) onSent?.();
        toast.error(
          `${delivered ? 'Envio parcial' : 'Erro ao enviar item do catálogo'}: ${r.errors[0]}${
            r.errors.length > 1 ? ` (+${r.errors.length - 1} falha(s))` : ''
          }`,
          { id: 'catalog-send', duration: 8000 }
        );
      }
    } catch (err: any) {
      console.error('[CatalogPicker] Erro ao enviar item:', err);
      toast.error(`Erro ao enviar item: ${err?.message || 'falha de conexão'}`, { id: 'catalog-send' });
    } finally {
      setSendingItemId(null);
    }
  };

  // Portal em document.body + z-index acima dos controles flutuantes da Inbox (z-[60]/[70]):
  // o modal estava dentro do stacking context do ChatArea (relative/overflow-hidden).
  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs" onClick={onClose}>
      <div
        className="bg-white dark:bg-[#1A1D21] border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-900/50">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-blue-50 dark:bg-blue-950/80 text-blue-600 dark:text-blue-400 rounded-xl">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white text-base">
                Enviar Item do Catálogo
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Selecione um produto ou serviço para enviar diretamente ao cliente.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters and Search Bar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1A1D21] flex flex-col sm:flex-row items-center gap-3">
          <div className="relative flex-1 w-full">
            <Search className="w-4 h-4 absolute left-3.5 top-2.5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar no catálogo..."
              className="w-full pl-9 pr-3.5 py-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center bg-gray-100 dark:bg-gray-900 p-1 rounded-xl shrink-0 border border-gray-200 dark:border-gray-800 w-full sm:w-auto">
            <button
              onClick={() => setTipoFilter('todos')}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                tipoFilter === 'todos'
                  ? 'bg-white dark:bg-[#1A1D21] text-gray-900 dark:text-white shadow-2xs'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setTipoFilter('produto')}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1 ${
                tipoFilter === 'produto'
                  ? 'bg-white dark:bg-[#1A1D21] text-blue-600 dark:text-blue-400 shadow-2xs'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              <Package className="w-3 h-3" />
              Produtos
            </button>
            <button
              onClick={() => setTipoFilter('servico')}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1 ${
                tipoFilter === 'servico'
                  ? 'bg-white dark:bg-[#1A1D21] text-emerald-600 dark:text-emerald-400 shadow-2xs'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              <Wrench className="w-3 h-3" />
              Serviços
            </button>
          </div>
        </div>

        {/* Content Items List Grid */}
        <div className="p-4 overflow-y-auto flex-1">
          {loading && items.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center text-gray-400 gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="text-xs font-medium">Buscando itens do catálogo...</span>
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-gray-400 space-y-1">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Nenhum item encontrado
              </p>
              <p className="text-xs text-gray-500">
                Ajuste os filtros ou cadastre novos produtos no módulo de Catálogo.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {items.map((item) => {
                const thumbnail = item.imagens && item.imagens.length > 0 ? item.imagens[0] : null;
                const isProduto = item.tipo === 'produto';
                const isSending = sendingItemId === item.id;

                return (
                  <div
                    key={item.id}
                    className="p-3 bg-gray-50/70 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800/80 border border-gray-200/80 dark:border-gray-800 rounded-xl transition-all flex items-center gap-3 group relative cursor-pointer"
                    onClick={() => !sendingItemId && handleSendItem(item)}
                  >
                    {/* Thumbnail */}
                    <div className="w-16 h-16 rounded-lg bg-gray-200 dark:bg-gray-800 shrink-0 overflow-hidden relative flex items-center justify-center">
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt={item.nome}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <ImageOff className="w-5 h-5 text-gray-400" />
                      )}
                      <span
                        className={cn(
                          'absolute top-1 left-1 px-1.5 py-0.5 rounded-md text-[9px] font-extrabold uppercase',
                          isProduto
                            ? 'bg-blue-600 text-white'
                            : 'bg-emerald-600 text-white'
                        )}
                      >
                        {isProduto ? 'Prod' : 'Serv'}
                      </span>
                    </div>

                    {/* Meta info */}
                    <div className="flex-1 min-w-0 space-y-1">
                      <h4 className="text-xs font-bold text-gray-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400">
                        {item.nome}
                      </h4>
                      {item.descricao && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                          {item.descricao}
                        </p>
                      )}
                      <div className="flex items-center justify-between pt-0.5">
                        <span className="text-xs font-extrabold text-gray-900 dark:text-white">
                          {formatCurrency(item.preco, item.moeda)}
                        </span>
                        {item.sku && (
                          <span className="text-[10px] font-mono text-gray-400">
                            #{item.sku}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Send Button */}
                    <button
                      type="button"
                      disabled={!!sendingItemId}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendItem(item);
                      }}
                      className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-xs transition-colors shrink-0 cursor-pointer disabled:opacity-50"
                      title="Enviar item ao cliente"
                    >
                      {isSending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
